<script setup lang="ts">
import type { SplashAdPayload, SplashCheckResponse } from '~/types/splash-ad'
import { isValidSplashAdPayload, parseSplashStickyPolicy } from '~/types/splash-ad'

// TEMP: enable splash check on web for testing.
const ENABLE_SPLASH_WEB_TEST = false

const userStore = useUserStore()
const baseStore = useBaseStore()
const consentStore = useConsentStore()
const { showOnboarding } = useOnboarding()
const scriptTag = 'script'

const route = useRoute()
const config = useRuntimeConfig()
const splashAd = ref<SplashAdPayload | null>(null)

const title = 'Glouton: Économiser et bien manger, c\'est notre recette.'
const description = 'Économisez en trouvant les recettes les plus avantageuses selon les aubaines des épiceries, parmi plus de 20000 en inventaire.'

useHead({
  title,
  meta: [
    { charset: 'UTF-8' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1, viewport-fit=cover' },
    { name: 'description', content: description },
    { name: 'author', content: 'Glouton' },
    { name: 'apple-mobile-web-app-title', content: 'Glouton' },
    { name: 'application-name', content: 'Glouton' },
    { name: 'msapplication-TileColor', content: '#F37A4E' },
    { name: 'theme-color', content: '#F37A4E' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:image', content: 'https://glouton.b-cdn.net/site/images/social.jpg' },
    { property: 'og:image:type', content: 'image/png' },
    { property: 'fb:app_id', content: '774021859466667' },
  ],
  link: [
    { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
    { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' },
    { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' },
    { rel: 'manifest', href: '/manifest.webmanifest' },
    { rel: 'mask-icon', href: '/safari-pinned-tab.svg', color: '#F37A4E' },
  ],
})


onMounted(async () => {
  baseStore.detectPlatform()

  // Initialize user from store when there's a token
  await userStore.initializeUser()

  const isNativePlatform = baseStore.platform === 'ios' || baseStore.platform === 'android'
  const shouldCheckSplash = isNativePlatform || ENABLE_SPLASH_WEB_TEST

  // Non-blocking splash check on native: sticky policy for everyone; fullscreen overlay only when allowed.
  if (shouldCheckSplash) {
    void (async () => {
      try {
        const splashResponse: SplashCheckResponse = await $fetch(`${config.public.apiUrl}/splash/check`, {
          method: 'GET',
        })
        baseStore.setNativeStickySplashPolicy(parseSplashStickyPolicy(splashResponse))
        if (Number(userStore.user?.extra) === 1)
          baseStore.setNativeStickySplashPolicy({ allow: false, delayMs: 0 })
        if (Number(splashResponse?.success) === 1 && isValidSplashAdPayload(splashResponse?.data)) {
          if (Number(userStore.user?.extra) !== 1)
            splashAd.value = splashResponse.data
        }
      }
      catch (error) {
        console.warn('Failed to fetch splash ad:', error)
      }
    })()
  }

  // If we have a token after initialization, refresh the user
  if (userStore.access_token) {
    await userStore.refreshUser(`${config.public.apiUrl}/auth/refresh`)
  }

  if (Number(userStore.user?.extra) === 1)
    baseStore.setNativeStickySplashPolicy({ allow: false, delayMs: 0 })

  try {
    const promoCode = typeof route.query.code === 'string' ? route.query.code.trim() : ''

    // Public promo only — menus / PromoBanner must not use a private ?code= promo
    const response = await $fetch(`${config.public.apiUrl}/home/check-promos`, {
      method: 'GET',
    }) as { success: number, status?: string, data?: any, message?: string }

    baseStore.promos = response.success === 1 ? response.data : null
    baseStore.promoStatus = response.success === 1 && response.data ? 'valid' : 'none'

    if (promoCode) {
      try {
        const linked = await $fetch(`${config.public.apiUrl}/home/check-promos`, {
          method: 'GET',
          query: { code: promoCode },
        }) as { success: number, status?: string, data?: any, message?: string }

        baseStore.linkedPromo = linked.data ?? null
        baseStore.linkedPromoStatus = (linked.status as typeof baseStore.linkedPromoStatus)
          || (linked.success === 1 ? 'valid' : 'invalid')
      }
      catch (linkedError) {
        console.warn('Failed to fetch linked promo:', linkedError)
        baseStore.linkedPromo = null
        baseStore.linkedPromoStatus = 'invalid'
      }
    }
    else {
      baseStore.linkedPromo = null
      baseStore.linkedPromoStatus = 'none'
    }
  }
  catch (error) {
    console.warn('Failed to fetch promos:', error)
    baseStore.promos = null
    baseStore.promoStatus = 'none'
  }
  finally {
    userStore.authChecked = true
  }
})

function closeSplashAd() {
  splashAd.value = null
}

// Function to check and show onboarding modal for current page
function checkOnboardingModal(pageName: string) {
  // Only execute if user is logged in
  if (!userStore.user)
    return

  // Only execute if onboarding_modals is not empty
  if (!userStore.user.onboarding_modals || userStore.user.onboarding_modals.length === 0)
    return

  // Check if user.onboarding_modals contains a page value that matches pageName
  // and the modal_key is not already viewed and version is within range
  const matchingModal = userStore.user?.onboarding_modals?.find((modal: any) => {
    // Check if modal should be shown based on Extra status (convert to number for comparison)
    const isUserExtra = Number(userStore.user?.extra) === 1 && Number(userStore.user?.extra_active_trial) === 0

    // Handle show_to_extra: show if undefined/null or truthy (1, "1", true). Hide if falsy (0, "0", false)
    const shouldShowToExtra = modal.show_to_extra === undefined || modal.show_to_extra === null || Boolean(Number(modal.show_to_extra))

    // Handle show_to_not_extra: show if undefined/null or truthy (1, "1", true). Hide if falsy (0, "0", false)
    const shouldShowToNotExtra = modal.show_to_not_extra === undefined || modal.show_to_not_extra === null || Boolean(Number(modal.show_to_not_extra))

    // If user is Extra, check if modal should be shown to Extra users
    if (isUserExtra && !shouldShowToExtra) {
      return false
    }

    // If user is not Extra, check if modal should be shown to non-Extra users
    if (!isUserExtra && !shouldShowToNotExtra) {
      return false
    }

    return modal.page === pageName
      && !userStore.user?.viewed_onboarding_modals?.includes(modal.modal_key)
      && baseStore.versionInt >= modal.min_version
      && baseStore.versionInt <= modal.max_version
  })

  if (matchingModal) {
    // Open onboarding modal with modal data
    showOnboarding({
      steps: matchingModal.steps,
      title: matchingModal.title || 'Découvrir les nouveautés',
      modalKey: matchingModal.modal_key,
      auto: true,
    })
  }
}

// Watch for route changes (client side only)
if (import.meta.client) {
  watch(() => route.name, (newRouteName) => {
    if (newRouteName) {
      checkOnboardingModal(newRouteName as string)
    }
  }, { immediate: true })

  // Watch for user login/status changes
  watch(() => userStore.user, (newUser, oldUser) => {
    // Trigger check when user logs in or their status changes
    if (newUser && (!oldUser || oldUser.id !== newUser.id || oldUser.extra !== newUser.extra)) {
      if (route.name) {
        checkOnboardingModal(route.name as string)
      }
    }
  })

  watch(
    () => Number(userStore.user?.extra),
    (extra) => {
      if (extra === 1)
        baseStore.setNativeStickySplashPolicy({ allow: false, delayMs: 0 })
    },
  )
}
</script>

<template>
  <div unit="rem" class="relative min-h-screen double-test">
    <ConsentPreferencesModal />
    <SplashAdOverlay v-if="splashAd" :ad="splashAd" @close="closeSplashAd" />
    <SearchModal />
    <div class="hidden xl:block xl:fixed">
      <SideBar />
    </div>

    <div class="xl:ml-55 relative bg-sel lg:min-h-screen xl:inset-ring-1 inset-ring-gray-200 pb-14 xl:pb-0">
      <NavBar class="w-full z-5 sticky top-0" />
      <slot />
    </div>
    <BottomNav />
    <GAMStickyBannerAd v-if="baseStore.platform === 'ios' || baseStore.platform === 'android'" />
    <!-- Scripts pub (teleport séparé pour limiter l’impact sur le re-render / icônes) -->
    <teleport
      v-if="userStore.authChecked && baseStore.showAds && baseStore.platform === 'web' && (!userStore.user || userStore.user.extra !== 1) && route.query.low !== 'yes'"
      to="head"
    >
      <component
        :is="scriptTag"
        type="text/javascript"
        src="https://rdc.m32.media/madops.min.js"
      />
      <component
        :is="scriptTag"
        v-if="consentStore.advertisingAllowed"
        type="text/javascript"
        async
        src="https://rdc.m32.media/m32pixel.min.js"
      />
      <component
        :is="scriptTag"
        type="text/javascript"
        async
        src="https://link.rubiconproject.com/magnite/23292.js"
      />
    </teleport>
  </div>
</template>
